import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ArrowDown02Icon, ArrowTurnBackwardIcon, ArrowUp02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { AnimatePresence } from 'motion/react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { createFormDataProcessor } from 'utils/formDataProcessor';
import { CustomButton } from 'views/CandidateApp/components/CustomButton/CustomButton';

import { FormMessage, MessageType, ValidationAlert } from 'components/FormComponents';
import FadeInMotion from 'components/Motion/FadeInMotion';
import NumberMotion from 'components/Motion/NumberMotion';
import ProgressBarMotion from 'components/Motion/ProgressBarMotion';
import ScaleMotion from 'components/Motion/ScaleMotion';
import SliderMotion from 'components/Motion/SliderMotion';

import QuestionWrapper from '../Questions/QuestionWrapper';
import styles from './Form.module.scss';

const FormWrapper = ({
  previewMode = false,
  hideHeader = false,
  hideLogo = false,
  hideFooter = false,
  question,
  questionIndex = 0,
  totalQuestions = 0,
  formCompleted = false,
  backward = false,
  formMessage = null,
  locale = 'en_GB',
  brand = {},
  keyboardHint = null,
  blockMessage = null,
  textParams = {},
  height = '100vh',
  getComponentProps = () => {},
  componentMap = {},
  onNext = () => {},
  onBack = () => {},
  allowTranslation = false,
  accumulatedEnhancedValues = {},
  selectedLanguageCode = null,
  translatedText = null,
  translatedDescription = null,
  translatedOptions = null,
  onLanguageChange = () => {},
  isTranslating = false,
}) => {
  const {
    control,
    handleSubmit,
    trigger,
    register,
    setError,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: {},
  });
  const { t } = useTranslation();
  const { token } = useParams();

  const formDataProcessor = useMemo(
    () => createFormDataProcessor('referees/token/' + token + '/language_detection'),
    [token],
  );

  const [isAnimationDisabled, setIsAnimationDisabled] = useState(() => {
    return JSON.parse(localStorage.getItem('isAnimationDisabled') || 'false');
  });

  const [blockMessageText, setBlockMessageText] = useState(null);

  useEffect(() => {
    setBlockMessageText(blockMessage ?? null);
  }, [blockMessage]);

  useEffect(() => {
    localStorage.setItem('isAnimationDisabled', JSON.stringify(isAnimationDisabled));
  }, [isAnimationDisabled]);

  useEffect(() => {
    if (formCompleted) {
      localStorage.removeItem('isAnimationDisabled');
    }
  }, [formCompleted]);

  const percentageCompleted = useMemo(() => {
    return Math.round((questionIndex / totalQuestions) * 100);
  }, [questionIndex, totalQuestions]);

  const containerRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = async event => {
      const isEditingOutsideForm =
        event.target.matches?.('input, textarea, select, [contenteditable="true"]') &&
        !containerRef.current?.contains(event.target);
      if (isEditingOutsideForm) return;

      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          onBack();
          break;
        case 'ArrowDown':
          event.preventDefault();
          await handleValidationAndNext();
          break;
        case 'Enter':
          if (event.shiftKey) break;
          event.preventDefault();
          await handleValidationAndNext();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [questionIndex]);

  const handleValidationAndNext = useCallback(async () => {
    const fieldName = question.id.toString();
    const valid = await trigger(fieldName);
    if (valid) {
      if (allowTranslation) {
        await handleSubmit(async formData => {
          const mappedData = await formDataProcessor(formData, question, accumulatedEnhancedValues);
          await onNext(mappedData);
        })();
      } else {
        await handleSubmit(onNext)();
      }
    }
  }, [
    question,
    trigger,
    allowTranslation,
    handleSubmit,
    formDataProcessor,
    accumulatedEnhancedValues,
    setError,
    onNext,
  ]);

  if (!question) return <div>{t('components.form.noQuestionsProvided')}</div>;
  if (formCompleted) return <div>{t('components.form.formCompleted')}</div>;

  const defaultLogo = brand?.brand_logo
    ? brand.brand_logo
    : 'assets/images/checkmate/icon-label.svg';

  const animationToggleIcon = isAnimationDisabled
    ? 'assets/images/icons/animation_disabled.svg'
    : 'assets/images/icons/animation_enabled.svg';

  const isPreviewOnly = hideHeader && hideFooter;
  return (
    <div
      ref={containerRef}
      className={`flex flex-col w-full ${isPreviewOnly ? 'p-4' : 'p-8'}`}
      style={{ height }}>
      {!hideHeader && (
        <div className="formHeader flex-shrink-0 d-flex align-items-center justify-content-between">
          <div>
            {!hideLogo && (
              <img
                src={defaultLogo}
                className={styles.formHeaderLogoImage}
                width="60"
                height="60"
                alt={brand?.name}
              />
            )}
          </div>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
                {t('components.form.animationToggle', {
                  animationToggleMode: isAnimationDisabled ? 'disabled' : 'enabled',
                })}
              </Tooltip>
            }>
            <img
              src={animationToggleIcon}
              className="primary-text u-pointer"
              width="30"
              height="30"
              alt="Animation toggle icon"
              onClick={() => setIsAnimationDisabled(!isAnimationDisabled)}
            />
          </OverlayTrigger>
        </div>
      )}
      <AnimatePresence mode="wait">
        <SliderMotion
          key={question.id}
          isBackward={backward}
          disabled={isAnimationDisabled}
          className="flex-grow-1 d-flex align-items-center justify-content-center">
          <form
            className={cn(styles.formWrapper, 'd-flex flex-column u-gap-large')}
            onSubmit={async e => {
              e.preventDefault();
              await handleSubmit(handleValidationAndNext)();
            }}>
            {formMessage && (
              <FormMessage
                message={formMessage}
                type={MessageType.Info}
                closeable={false}
                noMargin
              />
            )}
            <QuestionWrapper
              question={question}
              textParams={textParams}
              previewMode={previewMode}
              control={control}
              register={register}
              error={errors[question.id]}
              componentMap={componentMap}
              questionIndex={questionIndex}
              keyboardHint={keyboardHint}
              getComponentProps={getComponentProps}
              animationDisabled={isAnimationDisabled}
              locale={locale}
              allowTranslation={allowTranslation}
              selectedLanguageCode={selectedLanguageCode}
              translatedText={translatedText}
              translatedDescription={translatedDescription}
              translatedOptions={translatedOptions}
              onLanguageChange={onLanguageChange}
              isTranslating={isTranslating}
              brand={brand}
            />
            {blockMessageText && (
              <div
                className={`u-margin-top--small u-margin-bottom--small ${question?.type === 'dynamic_array' ? 'u-order-1' : ''}`}>
                <FadeInMotion key={question.id + 'block-message'} disabled={isAnimationDisabled}>
                  <ValidationAlert show={true} message={blockMessageText} />
                </FadeInMotion>
              </div>
            )}
            {!previewMode && (
              <div className="d-flex align-items-center justify-content-start u-gap">
                <div className={cn(styles.actionButtons, 'd-flex align-items-center u-gap')}>
                  {questionIndex > 0 && (
                    <ScaleMotion
                      keyProp="back-button"
                      className="d-flex u-gap flex-shrink-0 flex-0"
                      disabled={isAnimationDisabled}>
                      <CustomButton
                        onClick={onBack}
                        brand={brand}
                        marginTop={false}
                        className="u-font-weight-400"
                        icon="arrow-left"
                        width="auto"
                        onlyIcon={true}
                        rounded={true}
                        dataTestId="wizard-back-button"
                      />
                    </ScaleMotion>
                  )}
                  <ScaleMotion
                    keyProp="submit-button"
                    className={cn(styles.actionButton, 'd-flex u-gap flex-grow-1')}
                    disabled={isAnimationDisabled}>
                    <CustomButton
                      submit={true}
                      brand={brand}
                      marginTop={false}
                      className="u-font-weight-400"
                      icon="arrow-right"
                      prependIcon={false}
                      dataTestId="wizard-next-button">
                      {questionIndex === totalQuestions - 1 ? t('common.submit') : t('common.next')}
                    </CustomButton>
                  </ScaleMotion>
                </div>
                <span className="d-flex align-items-center text-tertiary u-font-size-sm u-font-weight-400 sm-hide white-space-pre-wrap">
                  <Trans
                    i18nKey="keyboard.hint.pressEnter"
                    components={[
                      <span className="text-tertiary u-font-weight-600" />,
                      <HugeiconsIcon size="14" icon={ArrowTurnBackwardIcon} strokeWidth="3" />,
                    ]}
                  />
                </span>
              </div>
            )}
          </form>
        </SliderMotion>
      </AnimatePresence>
      {!hideFooter && (
        <>
          <div className="formFooter flex-shrink-0 d-flex align-items-center justify-content-between">
            <span className="secondary-text u-font-size--small u-font-weight-500 d-flex align-items-center">
              {t('components.form.question')}&nbsp;
              <SliderMotion
                key={questionIndex}
                yAxis={20}
                isBackward={backward}
                disabled={isAnimationDisabled}
                duration={0.5}
                turnOffOpacityTransition={true}
                transition={{ scale: { type: 'spring', visualDuration: 0.5, bounce: 0.1 } }}>
                {questionIndex + 1}
              </SliderMotion>
              /{totalQuestions}
            </span>
            <div className="d-flex align-items-center u-gap sm-hide">
              <span className="d-flex align-items-center text-tertiary u-font-size-sm u-font-weight-400">
                <HugeiconsIcon size="14" icon={ArrowUp02Icon} strokeWidth="3" />
                <HugeiconsIcon
                  size="14"
                  icon={ArrowDown02Icon}
                  strokeWidth="3"
                  className="u-margin-right--small"
                />
                {t('components.form.navigateQuestions')}
              </span>
              <span className="d-flex align-items-center text-tertiary u-font-size-sm u-font-weight-400">
                <div className="u-padding--xSmall u-border-radius u-border-radius--xs-small u-border surface-secondary">
                  Tab
                </div>
                &nbsp;{t('components.form.tabToNavigate')}
              </span>
            </div>
            <span className="secondary-text u-font-size--small u-font-weight-500">
              {!isAnimationDisabled ? (
                <NumberMotion increaseto={percentageCompleted} />
              ) : (
                percentageCompleted
              )}
              {t('components.form.percentCompleted')}
            </span>
          </div>
          <div className="u-fixed u-bottom-0 u-left-0 u-right-0 surface-secondary">
            <ProgressBarMotion
              className="progress-bar__fill"
              dynamicWidth={percentageCompleted}
              disabled={isAnimationDisabled}
              duration={0.5}
              ease="easeInOut"
              style={{
                backgroundColor: brand?.color_button || '#4E18BB',
                height: '6px',
                borderRadius: '10px',
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default FormWrapper;
